import type { State } from './types'
import type { AuditLogEvent } from '../../domain/audit-log-event'

import dayjs from 'dayjs'

import * as constants from './constants'

export const formatDuration = (milliseconds: number, options: { short?: boolean } = {}): string => {
  const duration = dayjs.duration(milliseconds)
  const hours = Math.floor(duration.asHours())
  const minutes = duration.minutes()
  const seconds = duration.seconds()

  if (!options.short) {
    const parts = []
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`)
    if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`)
    if (seconds > 0) parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`)
    return parts.join(' ') || '0 seconds'
  }

  const parts = []
  if (hours > 0) parts.push(`${hours}H`)
  if (minutes > 0) parts.push(`${minutes}M`)
  if (seconds > 0) parts.push(`${seconds}S`)
  return parts.join(' ') || '0S'
}

export const buildStateFromSearchParams = (
  searchParams: URLSearchParams
): {
  filters: State['filters']
  searchQuery: State['searchQuery']
  pagination: State['pagination']
} => {
  const pagination: State['pagination'] = {
    currentPage: Number(searchParams.get('page')) || 0,
    pageSize: Number(searchParams.get('page-size')) || 10
  }

  const searchQuery = searchParams.get('search') ?? undefined

  const filters: State['filters'] = {
    time: {},
    actions: [],
    types: [],
    actors: []
  }

  const dateFrom = searchParams.get('time-from')

  if (dateFrom) {
    filters.time.from = dateFrom
  }

  const dateTo = searchParams.get('time-to')

  if (dateTo) {
    filters.time.to = dateTo
  }

  const actions = searchParams.get('actions')

  if (actions) {
    filters.actions = actions.split(',') as State['filters']['actions']
  }

  const types = searchParams.get('types')

  if (types) {
    filters.types = types.split(',') as State['filters']['types']
  }

  const actors = searchParams.get('actors')

  if (actors) {
    filters.actors = actors.split(',')
  }

  return {
    pagination,
    searchQuery,
    filters
  }
}

interface RawSearchParam {
  value: unknown
  name: string
  convert?: (value: unknown) => unknown
}

export const buildSearchParams = (rawSearchParams: RawSearchParam[]): URLSearchParams => {
  const searchParams = new URLSearchParams()

  for (const rawSearchParam of rawSearchParams) {
    if (Array.isArray(rawSearchParam.value) && rawSearchParam.value.length === 0) {
      searchParams.delete(rawSearchParam.name)
      continue
    }

    if (rawSearchParam.value === undefined) {
      searchParams.delete(rawSearchParam.name)
      continue
    }

    if (typeof rawSearchParam.value === 'string' && rawSearchParam.value.trim() === '') {
      searchParams.delete(rawSearchParam.name)
      continue
    }

    searchParams.set(rawSearchParam.name, String(rawSearchParam.value))
  }

  return searchParams
}

export const generateWeek = (
  startDay: number = constants.WEEK_START_DAY
): Array<(typeof constants.WEEK_DAY_NAMES)[number]> => {
  const days = constants.WEEK_DAY_NAMES.filter(Boolean)

  return [...days.slice(startDay), ...days.slice(0, startDay)]
}

export const renderGroupUpdateHumanFieldName = (title: string): string => {
  return constants.GROUP_UPDATE_FIELD_NAMES_MAP[title] ?? title
}

export const getGroupUpdateValueByPath = (
  resource: unknown,
  path: Array<string | number>
): unknown => {
  return path.reduce((acc, key) => {
    if (acc && typeof acc === 'object' && acc !== null && Object.hasOwn(acc, key)) {
      return (acc as Record<string, unknown>)[key]
    }

    return null
  }, resource)
}

export const buildGroupUpdateChanges = (
  changes: AuditLogEvent['changes']
): Record<string, AuditLogEvent['changes']> => {
  return (changes ?? []).reduce<Record<string, AuditLogEvent['changes']>>((acc, diff) => {
    const path = diff.path[0]

    if (path === undefined) {
      return acc
    }

    if (!acc[path]) {
      acc[path] = []
    }

    acc[path].push(diff)

    return acc
  }, {})
}

export const ordinal = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100

  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0] ?? '')
}

export const humanJoin = (items: string[]): string => {
  if (items.length === 0) {
    return ''
  }

  if (items.length === 1) {
    return items[0] || ''
  }

  if (items.length === 2) {
    return items.join(' and ')
  }

  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

export const isEmptySchedule = (schedule?: Record<string, number[]>): boolean => {
  return (
    !schedule ||
    Object.keys(schedule).length === 0 ||
    Object.values(schedule).every(w => !(w?.length ?? 0))
  )
}

export const humanReadableSchedule = (schedule: Record<string, number[]>): string[] => {
  const days = generateWeek()
  const weeks = Array.from({ length: 5 }, (_, i) => i + 1)

  const isEveryDay =
    Object.keys(schedule).length === days.length &&
    Object.values(schedule).every(w => (w?.length ?? 0) === weeks.length)

  if (isEveryDay) {
    return ['Every day']
  }

  const isNever = Object.keys(schedule).length === 0

  if (isNever) {
    return ['Never']
  }

  const result: string[] = []

  for (const day of days) {
    if (!day) {
      continue
    }

    const k = day.slice(0, 3).toUpperCase()
    const v = schedule[k] ?? []

    if (v.length === weeks.length) {
      result.push(`Every ${day}`)
      continue
    }

    if (v.length === 0) {
      continue
    }

    const n = v.map(week => ordinal(week))
    result.push(`Every ${humanJoin(n)} ${day}`)
  }

  return result
}

export const renderPrimitiveValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'boolean') {
    return value ? 'Enabled' : 'Disabled'
  }

  if (typeof value === 'object') {
    return JSON.stringify(value)
  }

  return 'N/A'
}
